import { LogManager, Model, ModelRow, UrlUtil, Currency, CurrencySettings } from "@mcleod/core";
import { ImageTable } from "@mcleod/common";
import { AutogenLayoutMiscInvoiceHistory } from "./autogen/AutogenLayoutMiscInvoiceHistory";
import { DataSourceMode, Layout, CrudDecorator } from "@mcleod/components";

interface MiscInvoiceHistoryNavigateProps {
    invoiceNoString?: string;
}

const layoutURL = "lme/ar/MiscInvoiceHistory";
const log = LogManager.getLogger("lme.ar.MiscInvoiceHistory");

export class MiscInvoiceHistory extends AutogenLayoutMiscInvoiceHistory {
    private segAllocControl: ModelRow;

    public static slideUp(props: MiscInvoiceHistoryNavigateProps): void {
        const url = `/${layoutURL}${UrlUtil.buildQueryString({
            mode: "update",
            ...(props.invoiceNoString ? { invoice_no_string: props.invoiceNoString } : {})
        })}`;

        const layout = Layout.getLayout(layoutURL) as MiscInvoiceHistory;
        const miscPanel = new CrudDecorator({
            layout: layout,
            mode: DataSourceMode.UPDATE,
            pseudoNavigateUrl: url,
            headerProps: {
                showClose: true,
                showSaveAndClose: true
            }
        });

        miscPanel.slideIn({ speed: 200 });
    }

    override onLoad() {
        this.initializeAllocationControl();
        (this.layoutImageTable as ImageTable).buttonUploadImage.visible = false;

        // Check for invoice_no_string in URL parameters and search if present
        const urlProps = UrlUtil.getPropsFromUrl(window.location.search);
        if (urlProps?.invoice_no_string) {
            this.searchByInvoiceNumber(urlProps.invoice_no_string);
        }
    }

    private async searchByInvoiceNumber(invoiceNoString: string) {
        try {
            if (this.mainDataSource) {
                await this.mainDataSource.search({ invoice_no_string: invoiceNoString });
            } else {
                log.error("Failed to search: mainDataSource not initialized. Ensure the layout is fully loaded before searching.");
            }
        } catch (error) {
            log.error(`Failed to search for invoice number ${invoiceNoString}:`, error);
        }
    }

    initializeAllocationControl() {
        Model.searchSingleRecord("lme/settlement/seg-alloc-control", null, { extraFields: "lme/dispatch/seg-alloc-control-single" }).then(response => {
            this.segAllocControl = response;
            this.textboxAllocationCode.visible = !!this.segAllocControl && this.segAllocControl.getBoolean("is_active", false);
        }).catch(error => {
            log.error("Failed to load segment allocation control settings:", error);
            this.segAllocControl = null;
        });
    }

    sourceMiscBillHistAfterExecution() {
        const rowId = this.activeRow?.get("id");
        if (rowId == null) {
            log.debug("No active row found for MISC_BILL_HIST population.");
            return;
        }
        (this.layoutImageTable as ImageTable).populateForRow("MISC_BILL_HIST", rowId);
    }

    sourceExciseTaxMiscBillHistAfterExecution() {
        this.calculateTotalTaxes();
    }

    private async calculateTotalTaxes() {
        const taxRows = this.sourceExciseTaxMiscBillHist?.data;

        if (!taxRows || taxRows.length === 0) {
            this.textboxTaxGridTotalCharges.text = new Currency(0).toString();
            return;
        }

        const currencyCode = this.mainDataSource.activeRow.getString("total_charges_c", CurrencySettings.getTransactionalCurrency());
        const billDate = this.mainDataSource.activeRow.getDate("bill_date", new Date());

        const totalTaxes = new Currency({
            amount: 0,
            currency_code: currencyCode,
            conversion_date: billDate
        });

        for (const taxRow of taxRows) {
            await totalTaxes.add(taxRow.getCurrency("amount"));
        }

        // Set the calculated total as text in the textbox
        this.textboxTaxGridTotalCharges.text = totalTaxes.toString();
    }
}
